import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GenericLoading } from 'components/ui/Loader';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { borrowersAPI } from 'services/swagger-api';
import { useContractInfosState } from '../ContractInfosContext';
import type { GetBorrowerContractDetailsResponse } from 'api/borrower';
import { NsKey } from 'enums/NsKey';
import {
  FileText,
  CreditCard,
  Shield,
  Banknote,
  TrendingDown,
  Building2,
  Users,
  ArrowUpRight,
} from 'lucide-react';

// ============================================
// HELPERS
// ============================================

const formatDate = (date?: Date | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatCurrency = (amount?: number): string => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const formatPercent = (value?: number): string => {
  if (value == null) return '-';
  return `${value.toFixed(3)} %`;
};

const formatDuration = (months?: number): string => {
  if (months == null) return '-';
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years === 0) return `${remainingMonths} mois`;
  if (remainingMonths === 0) return `${years} an${years > 1 ? 's' : ''}`;
  return `${years} an${years > 1 ? 's' : ''} et ${remainingMonths} mois`;
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem: FC<InfoItemProps> = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
    <p className="text-sm font-semibold text-slate-800 mt-0.5">{value}</p>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export const ContractBorrowerOverview: FC = () => {
  const { contractId } = useContractInfosState();
  const { t } = useTranslation(NsKey.Contract);
  const navigate = useNavigate();
  const [data, setData] = useState<GetBorrowerContractDetailsResponse>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const response = await borrowersAPI.getBorrowerContractDetails({
          contractId,
          includeQuestionnaire: false,
        });
        if (response) setData(response);
      } catch (error) {
        console.error('Erreur lors de la récupération des détails emprunteur:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [contractId]);

  if (isLoading || !data) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }

  const { contract, loans, specialConditions, otherBorrowerFiles } = data;

  return (
    <div className="space-y-6">
      {/* ====== CONTRACT ====== */}
      {contract && (
        <CardsSection
          title={t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.CONTRACT')}
          icon={<FileText size={18} className="text-primary" />}
          titleAside={
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${contract.statusCode === 'ACTIVE'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-amber-100 text-amber-700'
              }`}>
              {contract.status}
            </span>
          }
        >
          <div className="p-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.CONTRACT_NUMBER')} value={contract.contractNumber || '-'} />
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.PRODUCT')} value={contract.productLabel || '-'} />
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.EFFECTIVE_DATE')} value={formatDate(contract.effectiveDate)} />
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.END_DATE')} value={formatDate(contract.endDate)} />
            </div>
          </div>
        </CardsSection>
      )}

      {/* ====== LOANS ====== */}
      {loans && loans.length > 0 && (
        <CardsSection
          title={t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.LOANS')}
          icon={<Banknote size={18} className="text-primary" />}
          titleAside={
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">
              {t('CONTRACT.BORROWER_OVERVIEW.LOAN_COUNT', { count: loans.length })}
            </span>
          }
        >
          <div className="space-y-4">
            {loans.map((loan) => (
              <div
                key={loan.id}
                className="rounded-xl bg-white border border-slate-200 overflow-hidden"
              >
                {/* Loan header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                      {loan.internalNumber}
                    </span>
                    <span className="text-sm font-bold text-slate-800">
                      {formatCurrency(loan.loanAmount)}
                    </span>
                    {loan.loanType && (
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {loan.loanType}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-slate-500">
                    {formatDuration(loan.totalDuration)}
                  </span>
                </div>

                {/* Loan details grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                  <InfoItem
                    label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.INTEREST_RATE')}
                    value={`${formatPercent(loan.interestRatePercentage)}${loan.interestRateType ? ` (${loan.interestRateType})` : ''}`}
                  />
                  <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.QUOTA')} value={formatPercent(loan.quota)} />
                  <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.DEDUCTIBLE')} value={loan.deductible != null ? t('CONTRACT.BORROWER_OVERVIEW.DEDUCTIBLE_DAYS', { count: loan.deductible }) : '-'} />
                  <InfoItem
                    label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.DEFERMENT')}
                    value={`${loan.defermentPeriod ? t('CONTRACT.BORROWER_OVERVIEW.DEFERMENT_MONTHS', { count: loan.defermentPeriod }) : '-'}${loan.defermentType ? ` (${loan.defermentType})` : ''}`}
                  />
                </div>

                {/* Guarantees */}
                {loan.guarantees && loan.guarantees.length > 0 && (
                  <div className="px-4 pb-3">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">{t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.GUARANTEES')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {loan.guarantees.map((g) => (
                        <span
                          key={g.id}
                          className="inline-flex items-center gap-1 text-xs font-medium bg-primary/5 text-primary border border-primary/10 px-2.5 py-1 rounded-full"
                          title={g.label || ''}
                        >
                          <Shield size={11} />
                          {g.shortLabel || g.code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing */}
                {loan.pricing && (
                  <div className="px-4 py-3 border-t border-slate-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{t('CONTRACT.BORROWER_OVERVIEW.LABELS.AVG_MONTHLY_PREMIUM')}</p>
                        <p className="text-sm font-bold text-primary mt-0.5">{formatCurrency(loan.pricing.monthPrice)}</p>
                      </div>
                      <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.AVG_TAEA')} value={formatPercent(loan.pricing.averageRate)} />
                      <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.COST_EIGHT_YEARS')} value={formatCurrency(loan.pricing.totalPriceEightYears)} />
                      {loan.pricing.discountPercentage != null && loan.pricing.discountPercentage > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{t('CONTRACT.BORROWER_OVERVIEW.LABELS.DISCOUNT')}</p>
                          <p className="text-sm font-semibold text-emerald-600 mt-0.5 flex items-center gap-1">
                            <TrendingDown size={13} />
                            -{formatPercent(loan.pricing.discountPercentage)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardsSection>
      )}

      {/* ====== SPECIAL CONDITIONS ====== */}
      {specialConditions && (
        <CardsSection
          title={t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.SPECIAL_CONDITIONS')}
          icon={<CreditCard size={18} className="text-primary" />}
        >
          <div className="p-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.PROJECT_TYPE')} value={specialConditions.insuranceProjectType || '-'} />
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.START_DATE')} value={formatDate(specialConditions.startDate)} />
              <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.FREQUENCY')} value={specialConditions.paymentFrequency || '-'} />
              {specialConditions.dueDate && (
                <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.DUE_DATE')} value={t('CONTRACT.BORROWER_OVERVIEW.DUE_DATE_VALUE', { day: specialConditions.dueDate.day || '-', month: specialConditions.dueDate.month || '-' })} />
              )}
              {specialConditions.bankAccountIban && (
                <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.IBAN')} value={specialConditions.bankAccountIban} />
              )}
            </div>

            {/* Renegotiation sub-section */}
            {specialConditions.renegotiation && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Building2 size={13} />
                  {t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.RENEGOTIATION')}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.ENTITY')} value={specialConditions.renegotiation.entityName || '-'} />
                  <InfoItem label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.CURRENT_LOAN_NUMBER')} value={specialConditions.renegotiation.currentLoanNumber || '-'} />
                  <InfoItem
                    label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.COMMERCIAL_CONTACT')}
                    value={`${specialConditions.renegotiation.commercialFirstName || ''} ${specialConditions.renegotiation.commercialLastName || ''}`.trim() || '-'}
                  />
                  {(specialConditions.renegotiation.commercialPhone || specialConditions.renegotiation.commercialEmail) && (
                    <InfoItem
                      label={t('CONTRACT.BORROWER_OVERVIEW.LABELS.CONTACT_DETAILS')}
                      value={specialConditions.renegotiation.commercialPhone || specialConditions.renegotiation.commercialEmail || '-'}
                    />
                  )}
                </div>
              </div>
            )}

          </div>
        </CardsSection>
      )}

      {/* ====== OTHER BORROWER FILES (CO-BORROWERS) ====== */}
      {otherBorrowerFiles && otherBorrowerFiles.length > 0 && (
        <CardsSection
          title={t('CONTRACT.BORROWER_OVERVIEW.SECTIONS.OTHER_BORROWER_FILES')}
          icon={<Users size={18} className="text-primary" />}
          titleAside={
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">
              {t('CONTRACT.BORROWER_OVERVIEW.OTHER_FILE_COUNT', { count: otherBorrowerFiles.length })}
            </span>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherBorrowerFiles.map((file) => (
              <button
                key={file.contractId}
                type="button"
                onClick={() => navigate(`/mes-contrats/details/${file.contractId}`)}
                className="group flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-200 text-left transition-all hover:border-primary/40 hover:shadow-md"
              >
                {/* Initials avatar */}
                <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary text-sm font-bold uppercase">
                  {`${file.firstName?.charAt(0) ?? ''}${file.lastName?.charAt(0) ?? ''}` || '?'}
                </span>

                {/* Identity + file number */}
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800 truncate">
                      {`${file.firstName ?? ''} ${file.lastName ?? ''}`.trim() || '-'}
                    </span>
                    {file.status && (
                      <span className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${file.statusCode === 'ACTIVE'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                        }`}>
                        {file.status}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider mt-1">
                    {t('CONTRACT.BORROWER_OVERVIEW.LABELS.FILE_NUMBER')}
                  </span>
                  <span className="block text-xs font-semibold text-slate-600 truncate">
                    {file.fileNumber || file.contractNumber || '-'}
                  </span>
                </span>

                {/* Link affordance */}
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-70 transition-all group-hover:opacity-100 group-hover:translate-x-0.5">
                  {t('CONTRACT.BORROWER_OVERVIEW.VIEW_FILE')}
                  <ArrowUpRight size={14} />
                </span>
              </button>
            ))}
          </div>
        </CardsSection>
      )}
    </div>
  );
};
